import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { SwiperContainer } from 'swiper/element';
import { Navigation, Pagination } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';

import { ObjectService } from '../../services/object.service';
import { environment } from '../../../environments/environment';
import { MediaContext } from '../../models/media-context';

@Component({
  selector: 'app-object-media',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './object-media.component.html',
  styleUrls: ['./object-media.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ObjectMediaComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @Input() uuid!: string;

  /** Загруженные медиа-объекты */
  media: MediaContext[] = [];

  @ViewChild('swiperContainer', { static: false })
  private swiperContainer!: ElementRef<SwiperContainer>;

  isPrevDisabled = true;
  isNextDisabled = false;

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Конфиг Swiper web-component */
  readonly options: SwiperOptions = {
    modules: [Navigation, Pagination],
    slidesPerView: 'auto',
    spaceBetween: 30,
    watchOverflow: true,
    freeMode: { enabled: true },
    on: {
      slideChange: () => this.updateButtonStates()
    }
  };

  constructor(
    private objectService: ObjectService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  // lifecycle
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uuid']?.currentValue) {
      this.loadMediaLinksByUuid();
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;             

    import('swiper/element/bundle').then(({ register }) => {
      register();
      this.initializeSwiper();
    });
  }

  ngOnDestroy(): void {
    /* освобождаем blob:-URL, созданные в браузере */
    if (this.isBrowser) {
      this.media
        .filter(m => m.url.startsWith('blob:'))
        .forEach(m => URL.revokeObjectURL(m.url));
    }
  }

  // Swiper helpers
  slide(dir: 'prev' | 'next'): void {
    if (!this.isBrowser) return;
    const swiper = this.swiperContainer?.nativeElement?.swiper;
    dir === 'prev' ? swiper.slidePrev() : swiper.slideNext();
    this.updateButtonStates();
  }

  updateButtonStates(): void {
    if (!this.isBrowser) return;
    const swiper = this.swiperContainer?.nativeElement?.swiper;
    this.isPrevDisabled = swiper?.isBeginning ?? true;
    this.isNextDisabled = swiper?.isEnd ?? false;
  }

  private initializeSwiper(): void {
    if (!this.isBrowser) return;
    const el = this.swiperContainer.nativeElement as any;
    Object.assign(el, this.options);
    el.initialize?.();
  }

  private updateSwiper(): void {
    if (!this.isBrowser) return;
    this.swiperContainer?.nativeElement?.swiper?.update();
  }

  // Media
  private loadMediaLinksByUuid(): void {
    this.media = [];

    this.objectService.getImageLinksByUuid(this.uuid).subscribe((links) => {
      this.loadOneByOne(links, 0);
    });
  }

  private loadOneByOne(links: string[], index: number): void {
    if (index >= links.length) {
      this.updateSwiper();
      return;
    }

    const fullUrl = environment.backendUrl.replace('/api', '') + links[index];

    this.objectService.loadMediaByUrl(fullUrl).subscribe((m) => {
      this.media.push(m);
      this.updateSwiper();
      this.loadOneByOne(links, index + 1);
    });
  }
}